import Urn from '@adc/structures/urn'
import { match } from 'ts-pattern'
import { filters, FilterSchema } from '@adc/blueprint'
import * as graph from '@adc/sdk'
import logger from '@adc/logger'
import {
  fetchUsersByEmail,
  getEmailByUserId,
  getEmailByPartyId,
} from '../../providers/user'
import { fetchDatasetsForTask } from '../../providers/aiEvaluations'
const { FilterBuilder, FilterGroupBuilder } = filters.builders

async function getEmailOptionByUserId(userId: string): Promise<{
  value: string
  label: string
} | null> {
  return getEmailByUserId(userId).then(email => {
    if (!email || email instanceof Error) {
      return null
    }
    return {
      value: userId,
      label: email as string,
    }
  })
}

async function getEmailOptionByPartyUrn(partyUrn: string): Promise<{
  value: string
  label: string
} | null> {
  const partyId = Urn.safelyParse(partyUrn)?.identifier
  if (!partyId) {
    return null
  }
  return getEmailByPartyId(partyId).then(email => {
    if (!email || email instanceof Error) {
      return null
    }
    return {
      value: partyUrn,
      label: email as string,
    }
  })
}

async function getEmailOptionByPartyId(partyId: string): Promise<{
  value: string
  label: string
} | null> {
  if (!partyId) {
    return null
  }
  return getEmailByPartyId(partyId).then(email => {
    if (!email || email instanceof Error) {
      return null
    }
    return {
      value: partyId,
      label: email as string,
    }
  })
}

const getStatusOptions = () => {
  const options = filters.utils.deriveOptionsList(
    graph.enums.CognitionEvaluationGroupStatus
  )

  for (let i = 0; i < options.length; i++) {
    const icon = match(options[i].value)
      .with('COMPLETED', () => 'circle-check')
      .with('EXECUTED', () => 'circle-play')
      .with('IN_PROGRESS', () => 'bars-progress')
      .with('PENDING', () => 'hourglass')
      .with('FAILED', () => 'circle-xmark')
      .otherwise(() => undefined)

    if (icon) {
      ;(options[i] as Record<string, unknown>).icon = icon
    }
  }
  return options
}

const buildRankingClause = (
  state: unknown,
  def: FilterSchema.FilterDefinition
): FilterSchema.DSLClause | null => {
  const values = state as string[]
  if (!values || values.length === 0) return null

  // Map each option to a DSL clause
  const clauses = values
    .map(val => {
      switch (val) {
        case 'POSITIVE':
          return { range: { field: def.name, gte: 1 } }
        case 'NEGATIVE':
          return { range: { field: def.name, lte: -1 } }
        case 'ZERO':
          return { equals: { fields: def.name, values: [0] } }
        default:
          return null
      }
    })
    .filter(clause => clause !== null) as FilterSchema.DSLClause[]

  if (clauses.length === 0 || clauses.length === 3) return null
  if (clauses.length === 1) return clauses[0]

  // Combine multiple selections with OR logic
  return { or: { expressions: clauses } }
}

const statusFilterDefinition = new FilterGroupBuilder('status', 'Status')
  .description('Filter by status')
  .items([
    new FilterBuilder('status', 'multiSelect', 'string')
      .mode('checkboxButton')
      .options<'multiSelect'>(getStatusOptions())
      .styleIntent({ controlVariant: 'inline-radio' })
      .build(),
  ])
  .build()

// @ts-ignore next
const methodFilterDefinition = new FilterGroupBuilder('method', 'Method') // eslint-disable-line
  .description('Filter by method')
  .items([
    new FilterBuilder('evaluations.method', 'multiSelect', 'string')
      .options<'multiSelect'>(
        filters.utils.deriveOptionsList(graph.enums.CognitionEvaluationMethod)
      )
      .styleIntent({ controlVariant: 'inline-radio' })
      .build(),
  ])
  .build()

const usageFilterDefinition = new FilterGroupBuilder('usage', 'Usage')
  .description('Filter by Usage')
  .items([
    new FilterBuilder('authentication_status', 'multiSelect', 'string')
      .label('Authentication Status')
      .mode('checkboxButton')
      .styleIntent({
        layoutDirection: 'horizontal',
      })
      .options([
        {
          label: 'Logged Out',
          value: graph.enums.CognitionAuthenticationStatus.LoggedOut,
          icon: 'person-circle-question',
        },
        {
          label: 'Promoted',
          value: graph.enums.CognitionAuthenticationStatus.Promoted,
          icon: 'person-rays',
        },
        {
          label: 'Logged In',
          value: graph.enums.CognitionAuthenticationStatus.LoggedIn,
          icon: 'person-circle-check',
        },
      ])
      .build(),
    new FilterBuilder('owner_party_id', 'singleSelect', 'string')
      .label('Owner')
      .description('Search for users who ran (invoked) the evaluation')
      .placeholder('Search users...')
      .lookupMode('async')
      .options<'singleSelect'>([]) // No static options for async mode
      .onSearchChange(async (searchTerm: string) => {
        try {
          const users = await fetchUsersByEmail(searchTerm, 10)
          return (users as Array<{ party_id: string; email: string }>).map(
            (user: { party_id: string; email: string }) => ({
              value: user.party_id,
              label: user.email,
            })
          )
        } catch (error) {
          logger.error('Failed to fetch users:', error)
          return []
        }
      })
      .getOptionByValue(value => getEmailOptionByPartyId(String(value)))
      .searchDebounceMs(300) // Faster debounce for demo
      .minSearchLength(2) // Require at least 2 characters
      .build(),
    new FilterBuilder('created_by', 'singleSelect', 'string')
      .label('Created By')
      .description(
        'Search by users who created the evaluation. For datasets, the system is the creator.'
      )
      .placeholder('Search users...')
      .lookupMode('async')
      .options<'singleSelect'>([]) // No static options for async mode
      .onSearchChange(async (searchTerm: string) => {
        try {
          const users = await fetchUsersByEmail(searchTerm, 10)
          return (users as Array<{ user_id: string; email: string }>).map(
            (user: { user_id: string; email: string }) => ({
              value: user.user_id,
              label: user.email,
            })
          )
        } catch (error) {
          logger.error('Failed to fetch users:', error)
          return []
        }
      })
      .getOptionByValue(value => getEmailOptionByUserId(String(value)))
      .searchDebounceMs(300) // Faster debounce for demo
      .minSearchLength(2) // Require at least 2 characters
      .build(),
    new FilterBuilder('created_date', 'range', 'Date')
      .label('Created Date')
      .build(),
    new FilterBuilder('updated_date', 'range', 'Date')
      .styleIntent({ height: 'full' })
      .label('Updated Date')
      .build(),
  ])
  .build()

const reviewFilterDefinition = new FilterGroupBuilder(
  'review',
  'Assignment & Last Scored'
)
  .items([
    new FilterBuilder('assignee_urn.keyword', 'singleSelect', 'string')
      .label('Assigned To')
      .description('Search for users assigned to review the evaluation')
      .placeholder('Search users...')
      .lookupMode('async')
      .options<'singleSelect'>([]) // No static options for async mode
      .onSearchChange(async (searchTerm: string) => {
        try {
          const users = await fetchUsersByEmail(searchTerm, 10)
          return (users as Array<{ email: string; party_id: string }>).map(
            (user: { email: string; party_id: string }) => ({
              value: new Urn('party', user.party_id).toString(),
              label: user.email,
            })
          )
        } catch (error) {
          logger.error('Failed to fetch users:', error)
          return []
        }
      })
      .getOptionByValue(value => getEmailOptionByPartyUrn(String(value)))
      .searchDebounceMs(300) // Faster debounce for demo
      .minSearchLength(2) // Require at least 2 characters
      .allowNonExistentSearch(true, '(Unassigned)')
      .build(),
    new FilterBuilder('last_scored_at', 'range', 'Date')
      .label('Last Scored On')
      .styleIntent({ height: 'full' })
      .build(),
  ])
  .build()

const rankingFilterDefinition = new FilterGroupBuilder(
  'ranking',
  'Ranking & Comments'
)
  .items([
    new FilterBuilder('default_person_net_ranking', 'multiSelect', 'number')
      .label('End User Ranking')
      .mode('checkboxButton')
      .styleIntent({
        layoutDirection: 'horizontal',
      })
      .options([
        { label: 'Negative', value: 'NEGATIVE', icon: 'thumbs-down' },
        { label: 'Zero', value: 'ZERO', icon: 'minus' },
        { label: 'Positive', value: 'POSITIVE', icon: 'thumbs-up' },
      ])
      .buildDSLClause(buildRankingClause)
      .build(),
    new FilterBuilder('trusted_person_net_ranking', 'multiSelect', 'number')
      .label('Trusted User Net Ranking')
      .mode('checkboxButton')
      .styleIntent({
        layoutDirection: 'horizontal',
      })
      .options([
        { label: 'Negative', value: 'NEGATIVE', icon: 'thumbs-down' },
        { label: 'Zero', value: 'ZERO', icon: 'minus' },
        { label: 'Positive', value: 'POSITIVE', icon: 'thumbs-up' },
      ])
      .buildDSLClause(buildRankingClause)
      .build(),
    new FilterBuilder('system_net_ranking', 'multiSelect', 'number')
      .label('Judge Net Ranking')
      .mode('checkboxButton')
      .styleIntent({
        layoutDirection: 'horizontal',
      })
      .options([
        { label: 'Negative', value: 'NEGATIVE', icon: 'thumbs-down' },
        { label: 'Zero', value: 'ZERO', icon: 'minus' },
        { label: 'Positive', value: 'POSITIVE', icon: 'thumbs-up' },
      ])
      .buildDSLClause(buildRankingClause)
      .build(),
    new FilterBuilder('net_ranking', 'multiSelect', 'number')
      .label('Overall Net Ranking')
      .mode('checkboxButton')
      .styleIntent({
        layoutDirection: 'horizontal',
      })
      .options([
        { label: 'Negative', value: 'NEGATIVE', icon: 'thumbs-down' },
        { label: 'Zero', value: 'ZERO', icon: 'minus' },
        { label: 'Positive', value: 'POSITIVE', icon: 'thumbs-up' },
      ])
      .buildDSLClause(buildRankingClause)
      .build(),
    new FilterBuilder('comment_count', 'multiSelect', 'number')
      .label('Comments')
      .mode('checkboxButton')
      .options([
        { label: 'Has Comments', value: 'POSITIVE', icon: 'message' },
        { label: 'No Comments', value: 'ZERO', icon: 'message-slash' },
      ])
      .styleIntent({
        layoutDirection: 'horizontal',
      })
      .buildDSLClause((state, def) => {
        const values = state as string[]
        if (!values || values.length === 0) return null

        const hasComments = values.includes('POSITIVE')
        const noComments = values.includes('ZERO')
        if (hasComments && noComments) return null
        if (hasComments) return { range: { field: def.name, gte: 1 } }
        if (noComments) return { equals: { fields: def.name, values: [0] } }
        return null
      })
      .build(),
  ])
  .build()

const datasetFilterDefinition = (config: EvaluationFilterConfig) => {
  return new FilterGroupBuilder('dataset', 'Dataset')
    .description('Filter by Dataset')
    .items([
      new FilterBuilder('data_set.urn', 'singleSelect', 'string')
        .label('Dataset')
        .description('Filter by dataset used in the evaluation')
        .placeholder('Select dataset...')
        .options<'singleSelect'>([]) // No static options for async mode
        .lookupMode('asyncOnce')
        .cacheKey(config.task ?? 'no-task') // Triggers refetch when this changes
        .onSearchChange(async () => {
          if (!config.task) {
            return []
          }
          try {
            const results = await fetchDatasetsForTask(config.task)
            return results
              .map(dataset => ({
                value: dataset.urn,
                label: dataset.description || dataset.urn,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          } catch (error) {
            logger.error('Failed to fetch datasets:', error)
            return []
          }
        })
        .getOptionByValue(async value => {
          if (!config.task) {
            return null
          }
          try {
            const results = await fetchDatasetsForTask(config.task)
            const dataset = results.find(d => d.urn === value)
            if (!dataset) {
              return null
            }
            return {
              value: dataset.urn,
              label: dataset.description || dataset.urn,
            }
          } catch (error) {
            logger.error('Failed to fetch dataset by value:', error)
            return null
          }
        })
        .styleIntent({ controlVariant: 'inline-radio' })
        .build(),
    ])
    .build()
}

export type EvaluationFilterConfig = {
  task: string | null
}

export const definition: FilterSchema.FilterDefinitionGenerator<
  EvaluationFilterConfig
> = config => {
  return [
    statusFilterDefinition,
    usageFilterDefinition,
    reviewFilterDefinition,
    rankingFilterDefinition,
    datasetFilterDefinition(config ?? { task: null }),
  ]
}
